import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { toast } from 'react-toastify';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import { ServiceDataForm, defaultServiceDataForm } from '../../../app/models/Service';
import useServiceService from '../../../app/services/hooks/useServiceService';
import ServiceForm from './forms/ServiceForm';

type SelectOpt = { id: number | string; name: string };

interface Props {
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const ServiceCreate = ({ onSaved, onCancel }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { createService, storeService, fetchingCreateService, fetchingStoreService } = useServiceService();
    const [service, setService] = useState<ServiceDataForm>(defaultServiceDataForm);
    const [sectors, setSectors] = useState<SelectOpt[]>([]);
    const [itemCategories, setItemCategories] = useState<SelectOpt[]>([]);
    const [units, setUnits] = useState<SelectOpt[]>([]);
    const [taxes, setTaxes] = useState<SelectOpt[]>([]);
    const [errorFields, setErrorFields] = useState<any>();

    useEffect(() => {
        if (showLoading) showLoading('loading', 'Cargando formulario...');
        createService({
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setSectors(response.data?.sectors ?? []);
                setItemCategories(response.data?.item_categories ?? []);
                setUnits(response.data?.units ?? []);
                setTaxes(response.data?.taxes ?? []);
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                onCancel?.();
            }
        });
    }, []);

    const store = () => {
        const requiredErrors: Record<string, string[]> = {};
        if (!service.name?.trim()) requiredErrors.name = ['El nombre es obligatorio.'];
        if (service.sector_id == null || service.sector_id === 0) requiredErrors.sector_id = ['El sector es obligatorio.'];
        if (service.item_category_id == null || service.item_category_id === 0) requiredErrors.item_category_id = ['La categoría es obligatoria.'];
        if (service.unit_id == null || service.unit_id === 0) requiredErrors.unit_id = ['La unidad de medida es obligatoria.'];
        if (Object.keys(requiredErrors).length > 0) {
            setErrorFields(requiredErrors);
            return;
        }
        setErrorFields(undefined);
        if (showLoading) showLoading('loading', 'Guardando servicio...');
        storeService(service, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message ?? 'Servicio creado correctamente.' });
                toast.success(response.message ?? 'Servicio creado.', { autoClose: 2500 });
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
            },
            onFieldError: (res: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(res.data);
            }
        });
    };

    return (
        <>
            {!fetchingCreateService && (
                <ServiceForm
                    service={service}
                    setService={setService}
                    sectors={sectors}
                    itemCategories={itemCategories}
                    units={units}
                    taxes={taxes}
                    errorFields={errorFields}
                />
            )}
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={() => onCancel?.()} locked={fetchingStoreService} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={store} locked={fetchingStoreService || fetchingCreateService} />
                </div>
            </div>
        </>
    );
};

export default ServiceCreate;
